import { create } from 'zustand';

// Standard TEF Liquid Glow Glass Theme
const defaultTheme = {
  id: 'default-tef',
  name: 'TEF Default Theme',
  tokens: {
    // Colors
    primary: '#06b6d4',          // Brand Color
    primaryGlow: 'rgba(6,182,212,0.4)',
    secondary: '#a855f7',        // Accent Color
    secondaryGlow: 'rgba(168,85,247,0.4)',
    warning: '#f59e0b',
    error: '#ef4444',
    
    // Backgrounds & Surfaces
    bgBase: '#08081a',           // Dark Deep Background
    surface1: 'rgba(255, 255, 255, 0.03)', // Card Background Base
    surface2: 'rgba(255, 255, 255, 0.08)', // Card Hover / Accent
    
    // Text Colors
    textMain: '#ffffff',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    textAccent: '#67e8f9',       // Light primary text
    
    // Borders
    borderGlass: 'rgba(255, 255, 255, 0.1)',
    borderPrimary: 'rgba(6,182,212,0.3)',
    
    // Typography
    fontBody: "'Inter', sans-serif",
    fontHeading: "'Inter', 'Outfit', sans-serif",
    
    // Layout
    radiusCard: '24px',
    radiusButton: '12px',
    glassBlur: 'blur(20px)',
  }
};

export const useThemeStore = create((set, get) => ({
  themes: [], // Alle aus der DB geladenen Themes
  activeThemeId: null,
  activeTokens: defaultTheme.tokens, // Die aktuell wirksamen Variablen
  isLoading: false,

  // Styles aus der DB fetchen
  fetchThemes: async () => {
    set({ isLoading: true });
    try {
      const token = localStorage.getItem('tef_jwt') || localStorage.getItem('tef-auth-token') || localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/designer/global-styles', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok && data.styles) {
          set({ themes: [defaultTheme, ...data.styles] });
          const active = get().activeThemeId;
          if (!active && data.styles.length > 0) {
            get().applyTheme(data.styles[0].id);
          } else {
            get().applyTheme(defaultTheme.id);
          }
        }
      } else {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('tef_jwt');
          localStorage.removeItem('tef-auth-token');
          window.dispatchEvent(new CustomEvent('tef:auth-expired'));
        }
      }
    } catch (e) {
      console.error('Fehler beim Laden der Themes', e);
      set({ themes: [defaultTheme] });
    } finally {
      set({ isLoading: false });
    }
  },

  // Ein Theme anwenden und CSS-Variablen ins Dokument rooten
  applyTheme: (themeId) => {
    const { themes } = get();
    const theme = themes.find(t => t.id === themeId) || defaultTheme;
    const tokens = theme.tokens;
    
    set({ activeThemeId: themeId, activeTokens: tokens });
    
    // Native CSS Variables ins Document injizieren
    const root = document.documentElement;
    Object.entries(tokens).forEach(([key, value]) => {
      // Wandelt 'primaryGlow' in '--tef-primary-glow' um
      const cssVarName = `--tef-${key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}`;
      root.style.setProperty(cssVarName, value);
    });
  },

  // Einzelnen Token temporär ändern (z.B. Slider im Theme-Builder)
  updateToken: (key, value) => {
    const newTokens = { ...get().activeTokens, [key]: value };
    set({ activeTokens: newTokens });
    
    // Sofortige Live-Performance im CSS Root
    const cssVarName = `--tef-${key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`)}`;
    document.documentElement.style.setProperty(cssVarName, value);
  },

  // Aktuell gewähltes Theme (inkl. live editierten Tokens) speichern
  saveActiveTheme: async (nameToSave) => {
    const { activeThemeId, activeTokens, themes } = get();
    const isDefault = activeThemeId === defaultTheme.id;
    
    const payload = {
      id: isDefault ? null : activeThemeId, // Default Theme darf nicht überschrieben werden
      name: nameToSave || 'Custom Theme',
      tokens: activeTokens
    };

    try {
      set({ isLoading: true });
      const token = localStorage.getItem('tef_jwt') || localStorage.getItem('tef-auth-token') || localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/designer/global-styles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.ok) {
        // Reload list
        get().fetchThemes();
      }
    } catch(e) {
      console.error(e);
      alert('Fehler beim Speichern des Themes');
    } finally {
      set({ isLoading: false });
    }
  }

}));
